import { useState, useEffect, useRef, useCallback } from "react";
import { type ApiError } from "@/features/api/apiSlice";
import { UnitItem } from "@/components/units/unit/UnitItem";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { UnitItemLoading } from "@/components/units/unit/UnitItemLoading";
import { SkeletonUnitItem } from "@/components/units/unit/SkeletonUnitItem";
import { NoUnitsAvailable } from "./NoUnitsAvailable";
import { UnitError } from "@/components/units/unit/UnitError";
import type { UnitGroup } from "@/types";
import { UnavailableUnitItem } from "@/components/units/unit/UnavailableUnitItem";
import { filterUnitsByFeatureCategoryFacility } from "@/utils/units/unitFilters";
import { sortUnitsByProperty } from "@/utils/units/unitSorting";
import {
    pinFeaturedToTop,
    getFeaturedConfig,
} from "@/utils/units/featuredUnits";
import ErrorBoundary from "../ErrorBoundary";
import type { SerializedError } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import {
    selectSelectedFeatures,
    selectSelectedCategories,
    selectSelectedFacilities,
    selectSelectedSort,
    selectSelectedFeatured,
} from "@/features/filters/filtersSlice";
import { GridPromoExpansion } from "@/components/units/unit/GridPromoExpansion";
import { GridNearbyExpansion } from "@/components/units/unit/GridNearbyExpansion";
import { FacilitySection } from "@/components/facility/FacilitySection";
import { groupByFacility, cheapestUnits } from "@/utils/units/groupByFacility";
import { TableLayout, WidgetLayout } from "@/types/enums";
import type { PromoExpandedBy } from "@/context/UnitItemContext";

type UnitTableProps = {
    units: UnitGroup[];
    unitsError: ApiError | SerializedError | undefined;
    unitsIsLoading: boolean;
    initialUnitGroups?: UnitGroup[];
};

// Helper to check if error is ApiError
const isApiError = (error: ApiError | SerializedError): error is ApiError => {
    return "status" in error;
};

export const UnitTable = ({
    units,
    unitsError,
    unitsIsLoading,
    initialUnitGroups,
}: UnitTableProps) => {
    const displayOptions = useDisplayOptionContext();

    // Lifted state for grid promo expansion — tracks which unit's promo panel is
    // open (only one at a time) so the expansion renders as a full-width sibling
    // in the parent grid. selectedPromo is lifted from the card too: Layout 1's
    // form is form-only and uses the promo chosen on the card's "Choose Your
    // Special" dropdown, which lives in UnitItem — so the card passes it up here
    // for the UnitTable-rendered sibling to submit.
    const [gridExpandedUnit, setGridExpandedUnitRaw] = useState<{
        unitGroupId: number;
        expandedBy: PromoExpandedBy;
        selectedPromo: number;
    } | null>(null);

    // Mutually-exclusive nearby-units expansion across rows.
    const [nearbyExpandedUnitId, setNearbyExpandedUnitId] = useState<
        number | null
    >(null);

    const handleNearbyToggle = useCallback((unitGroupId: number) => {
        setNearbyExpandedUnitId((current) =>
            current === unitGroupId ? null : unitGroupId,
        );
    }, []);

    // Controls whether the expansion card is visually open (for CSS transition)
    const [isPromoOpen, setIsPromoOpen] = useState(false);
    const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const COLLAPSE_DURATION = 400; // ms — matches CSS collapse transition

    const setGridExpandedUnit = useCallback(
        (
            value: {
                unitGroupId: number;
                expandedBy: PromoExpandedBy;
                selectedPromo: number;
            } | null,
        ) => {
            // Clear any pending collapse timer
            if (collapseTimerRef.current) {
                clearTimeout(collapseTimerRef.current);
                collapseTimerRef.current = null;
            }

            if (value === null) {
                // Closing: animate collapse, then unmount
                setIsPromoOpen(false);
                collapseTimerRef.current = setTimeout(() => {
                    setGridExpandedUnitRaw(null);
                }, COLLAPSE_DURATION);
            } else if (
                gridExpandedUnit &&
                gridExpandedUnit.unitGroupId === value.unitGroupId
            ) {
                // Same card, just an updated value (e.g. the card's promo dropdown
                // changed while open) — update in place, no collapse/expand animation.
                setGridExpandedUnitRaw(value);
            } else if (
                gridExpandedUnit &&
                gridExpandedUnit.unitGroupId !== value.unitGroupId
            ) {
                // Switching to a different card:
                // 1. Collapse current (300ms CSS transition)
                // 2. Wait for collapse to finish
                // 3. Swap content
                // 4. Brief pause for DOM update
                // 5. Expand new (300ms CSS transition)
                setIsPromoOpen(false);
                collapseTimerRef.current = setTimeout(() => {
                    setGridExpandedUnitRaw(value);
                    collapseTimerRef.current = setTimeout(() => {
                        requestAnimationFrame(() => setIsPromoOpen(true));
                    }, 100);
                }, COLLAPSE_DURATION);
            } else {
                // Opening fresh (no current expansion)
                setGridExpandedUnitRaw(value);
                requestAnimationFrame(() => setIsPromoOpen(true));
            }
        },
        [gridExpandedUnit],
    );

    const isGrid = displayOptions.tableLayout === TableLayout.GRID;
    const isGridLayout2 =
        isGrid && displayOptions.widgetLayout === WidgetLayout.LAYOUT_2;

    // Grid column count (cards per row), layout-aware. Drives the full-width
    // sibling's reserved-row placement, so it MUST match the actual cards-per-row
    // the host CSS grid wraps to — a mismatch mis-places cards (the "card/form/card
    // split" bug). ⚠️ SYNC: these breakpoints mirror the host grid CSS (theme's
    // CUSTOM breakpoints from storage-theme `_variables.css`, NOT Tailwind defaults).
    // If the host grid template changes, change these too (and vice versa):
    //   Layout 2 (`unitTable-layout-2.css` grid md→2/xl→3/2xl→2): 768→2, 1024→3, 1280→2.
    //   Layout 1 (`unitTable.css` `:has(.unit-item--grid){ sm:grid-cols-4 lg:grid-cols-6
    //     2xl:grid-cols-8 }` + `.unit-item--grid{ col-span-2 }`): 640→2, 920→3, 1280→4.
    const getGridColumnCount = (): number => {
        if (typeof window === "undefined") return 1;
        if (displayOptions.widgetLayout === WidgetLayout.LAYOUT_2) {
            if (window.matchMedia("(min-width: 1280px)").matches) return 2;
            if (window.matchMedia("(min-width: 1024px)").matches) return 3;
            if (window.matchMedia("(min-width: 768px)").matches) return 2;
            return 1;
        }
        if (window.matchMedia("(min-width: 1280px)").matches) return 4;
        if (window.matchMedia("(min-width: 920px)").matches) return 3;
        if (window.matchMedia("(min-width: 640px)").matches) return 2;
        return 1;
    };

    const [gridColumnCount, setGridColumnCount] = useState(getGridColumnCount);

    useEffect(() => {
        if (!isGrid) return;

        // Superset of both layouts' grid breakpoints (theme custom values).
        const breakpoints = [640, 768, 920, 1024, 1280].map((px) =>
            window.matchMedia(`(min-width: ${px}px)`),
        );

        const handleChange = () => setGridColumnCount(getGridColumnCount());
        breakpoints.forEach((mq) =>
            mq.addEventListener("change", handleChange),
        );
        return () =>
            breakpoints.forEach((mq) =>
                mq.removeEventListener("change", handleChange),
            );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGrid]);

    // How the NEARBY grid expansion renders (promo always uses the sibling — see
    // gridPromoId below):
    //  - Sibling (full-width push-down): layout 2 always, and layout 1 at
    //    exactly 2 columns (640-919px) — its real 4-track container makes the
    //    cards' `span 2` placement well-defined.
    //  - Overlay (anchored to the card, no reflow): layout 1 at 1 column (where
    //    the card is already full width) and at 3-4 columns.
    const gridExpandsAsSibling =
        isGridLayout2 || (isGrid && gridColumnCount === 2);
    const gridExpandsAsOverlay = isGrid && !gridExpandsAsSibling;

    // Drives the CSS open/collapse animation for the grid sibling, mirroring
    // the promo expansion above.
    const [isNearbyOpen, setIsNearbyOpen] = useState(false);
    const nearbyCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    // Nearby and the promo/quote expansion are mutually exclusive in grid:
    // opening one hard-closes the other so two full-width siblings never land
    // on the same grid row.
    const hardClosePromo = useCallback(() => {
        if (collapseTimerRef.current) {
            clearTimeout(collapseTimerRef.current);
            collapseTimerRef.current = null;
        }
        setIsPromoOpen(false);
        setGridExpandedUnitRaw(null);
    }, []);

    const hardCloseNearby = useCallback(() => {
        if (nearbyCollapseTimerRef.current) {
            clearTimeout(nearbyCollapseTimerRef.current);
            nearbyCollapseTimerRef.current = null;
        }
        setIsNearbyOpen(false);
        setNearbyExpandedUnitId(null);
    }, []);

    // Promo-expand wrapper for grid: closes any open nearby panel first, then
    // animates the full-width sibling (mount → rAF open → delayed unmount via
    // setGridExpandedUnit). Used by both layouts now.
    const handleGridPromoExpand = useCallback(
        (
            value: {
                unitGroupId: number;
                expandedBy: PromoExpandedBy;
                selectedPromo: number;
            } | null,
        ) => {
            if (value !== null) hardCloseNearby();
            setGridExpandedUnit(value);
        },
        [hardCloseNearby, setGridExpandedUnit],
    );

    // Nearby toggle for grid; closes any open promo expansion on open.
    // Sibling mode animates (mount → rAF open → delayed unmount); overlay mode
    // toggles instantly.
    const handleGridNearbyToggle = useCallback(
        (unitGroupId: number) => {
            if (nearbyCollapseTimerRef.current) {
                clearTimeout(nearbyCollapseTimerRef.current);
                nearbyCollapseTimerRef.current = null;
            }
            const willOpen = nearbyExpandedUnitId !== unitGroupId;
            if (willOpen) hardClosePromo();

            if (!gridExpandsAsSibling) {
                setIsNearbyOpen(willOpen);
                setNearbyExpandedUnitId(willOpen ? unitGroupId : null);
                return;
            }

            if (willOpen) {
                setNearbyExpandedUnitId(unitGroupId);
                requestAnimationFrame(() => setIsNearbyOpen(true));
            } else {
                setIsNearbyOpen(false);
                nearbyCollapseTimerRef.current = setTimeout(() => {
                    setNearbyExpandedUnitId(null);
                }, COLLAPSE_DURATION);
            }
        },
        [nearbyExpandedUnitId, hardClosePromo, gridExpandsAsSibling],
    );

    const selectedFeatures = useSelector(selectSelectedFeatures);
    const selectedCategories = useSelector(selectSelectedCategories);
    const selectedFacilities = useSelector(selectSelectedFacilities);
    const selectedFeatured = useSelector(selectSelectedFeatured);
    const sort = useSelector(selectSelectedSort);

    // Extract sorting property and order from the sort string
    const [sortProperty, sortOrder] = sort.split(":") as [
        "vacancy" | "price" | "area",
        "asc" | "desc",
    ];

    const urlParams = new URLSearchParams(window.location.search);
    const unitTypeName = urlParams.get("unit_type_name");

    // Compute filtered and sorted units
    const filteredUnits = filterUnitsByFeatureCategoryFacility(
        units.filter((unitGroup: UnitGroup) => {
            if (
                unitTypeName &&
                unitGroup.representative_unit.unit_type_name !== unitTypeName
            ) {
                return false;
            }
            return displayOptions.unavailableUnits || unitGroup.available > 0;
        }) || [],
        selectedFeatures,
        selectedCategories,
        selectedFacilities,
        selectedFeatured,
        displayOptions.featuredUnits,
    );

    const sortedUnits = sortUnitsByProperty(
        filteredUnits,
        sortProperty,
        sortOrder,
    );

    // Pin featured units to the top when the CMS toggle is on, keeping the overall sort
    // order among themselves (pinFeaturedToTop is a stable partition applied after sorting).
    // Applies to both treatments (Tab and Badge) — pinning is the editor's choice.
    const filteredAndSortedUnits = displayOptions.pinFeaturedToTop
        ? pinFeaturedToTop(sortedUnits, displayOptions.featuredUnits)
        : sortedUnits;

    // Clear the nearby expansion if its unit drops out of the filtered/sorted set.
    useEffect(() => {
        if (
            nearbyExpandedUnitId !== null &&
            !filteredAndSortedUnits.some(
                (ug) => ug.representative_unit.id === nearbyExpandedUnitId,
            )
        ) {
            setNearbyExpandedUnitId(null);
            setIsNearbyOpen(false);
        }
    }, [filteredAndSortedUnits, nearbyExpandedUnitId]);

    // Same for the promo expansion: if its unit is filtered/sorted out while
    // open, hard-close it so stale gridExpandedUnit/isPromoOpen don't linger.
    useEffect(() => {
        if (
            gridExpandedUnit !== null &&
            !filteredAndSortedUnits.some(
                (ug) =>
                    ug.representative_unit.id === gridExpandedUnit.unitGroupId,
            )
        ) {
            hardClosePromo();
        }
    }, [filteredAndSortedUnits, gridExpandedUnit, hardClosePromo]);

    // Apply the same filtering and sorting pipeline to initialUnitGroups
    const sortedInitialGroups = initialUnitGroups
        ? sortUnitsByProperty(
              filterUnitsByFeatureCategoryFacility(
                  initialUnitGroups.filter((unitGroup: UnitGroup) => {
                      if (
                          unitTypeName &&
                          unitGroup.representative_unit.unit_type_name !==
                              unitTypeName
                      ) {
                          return false;
                      }
                      return (
                          displayOptions.unavailableUnits ||
                          unitGroup.available > 0
                      );
                  }),
                  selectedFeatures,
                  selectedCategories,
                  selectedFacilities,
                  selectedFeatured,
                  displayOptions.featuredUnits,
              ),
              sortProperty,
              sortOrder,
          )
        : [];

    const processedInitialGroups = displayOptions.pinFeaturedToTop
        ? pinFeaturedToTop(sortedInitialGroups, displayOptions.featuredUnits)
        : sortedInitialGroups;

    // Render UI
    if (unitsError) {
        const errorMessage = isApiError(unitsError)
            ? `Error ${unitsError.status}: ${unitsError.message}`
            : `Error: ${unitsError.message || "An unknown error occurred"}`;

        return (
            <div className="unit-table">
                <UnitError />
                <p>{errorMessage}</p>
            </div>
        );
    }

    if (unitsIsLoading) {
        // Market template (WEB-704): mirror the market card layout while units
        // load. The per-facility header (banner image, name, address, feature
        // pills, "See All Units") comes from the host-provided facilityMap,
        // which is available before the unit fetch resolves — so we reuse the
        // real FacilitySection chrome and only skeletonize the unit rows. This
        // keeps the loading state on-brand with the market table instead of a
        // flat list of generic skeleton rows.
        if (displayOptions.marketMode && displayOptions.facilityIds.length > 0) {
            const rowLayout = displayOptions.tableLayout ?? "row";
            return (
                <div className="unit-table unit-table--market">
                    {displayOptions.facilityIds.map((facilityId) => (
                        <FacilitySection
                            key={facilityId}
                            facilityId={facilityId}
                            hasUnits
                        >
                            {Array.from({ length: 3 }, (_, i) => (
                                <UnitItemLoading
                                    key={`market-loading-${facilityId}-${i}`}
                                    layout={rowLayout}
                                />
                            ))}
                        </FacilitySection>
                    ))}
                </div>
            );
        }

        // If we have cached unit groups that survive filtering, show data-driven skeleton cards
        if (processedInitialGroups.length > 0) {
            return (
                <div className="unit-table">
                    {processedInitialGroups.map((unitGroup: UnitGroup) => (
                        <SkeletonUnitItem
                            key={unitGroup.representative_unit.id}
                            unitGroup={unitGroup}
                            layout={displayOptions.tableLayout ?? "row"}
                        />
                    ))}
                </div>
            );
        }

        // Fallback to generic skeleton loaders when no cached data or all filtered out
        return (
            <div className="unit-table">
                {Array.from({ length: 6 }, (_, index) => (
                    <UnitItemLoading
                        layout={displayOptions.tableLayout ?? "row"}
                        key={`loading-item-${index}`}
                    />
                ))}
            </div>
        );
    }

    if (!units || units.length === 0) {
        return (
            <div className="unit-table">
                <UnitError />
            </div>
        );
    }

    // Market template renders per-facility sections (see the market branch
    // below). It needs at least one CMS facility id to section by; without
    // any, fall back to the flat table.
    const marketSections =
        !!displayOptions.marketMode && displayOptions.facilityIds.length > 0;

    // All units filtered out: the flat table shows its single table-wide
    // empty state. Market mode falls THROUGH to the sections instead, so
    // every facility keeps its header (each empty section reuses the same
    // empty-state copy).
    if (filteredAndSortedUnits.length === 0 && !marketSections) {
        return (
            <div className="unit-table">
                <ErrorBoundary>
                    <NoUnitsAvailable />
                </ErrorBoundary>
            </div>
        );
    }

    // Grid expansions (promo/nearby siblings + card overlays) are disabled in
    // market mode: their explicit gridRow/gridColumn placement is computed
    // from FLAT-list indices, but market rows render with section-local
    // indices, so an open expansion would misplace every card and the sibling
    // never mounts inside a section. Gating here keeps expansionRow at -1 and
    // (via the row props below) removes the triggers — UnitItem degrades to
    // no-trigger when the handlers are undefined.
    const gridExpansionEnabled = isGrid && !displayOptions.marketMode;

    // The promo/quote and nearby expansions are mutually exclusive in grid;
    // whichever is open determines the sibling's row and the downward shift of
    // the cards after it.
    // Promo sibling renders in BOTH grid layouts (push-down, in normal flow).
    const gridPromoId = gridExpansionEnabled
        ? (gridExpandedUnit?.unitGroupId ?? null)
        : null;
    // Nearby renders as a full-width sibling when gridExpandsAsSibling (layout 2
    // always; layout 1 at 2 columns). Otherwise it overlays the card (handled
    // in UnitItem), so there's no sibling here.
    const gridNearbyId =
        gridExpansionEnabled && gridExpandsAsSibling
            ? nearbyExpandedUnitId
            : null;
    const activeExpandedId = gridPromoId ?? gridNearbyId;

    // Find the expanded unit group and its index (for grid sibling expansion)
    const expandedIndex =
        activeExpandedId !== null
            ? filteredAndSortedUnits.findIndex(
                  (ug) => ug.representative_unit.id === activeExpandedId,
              )
            : -1;
    const expandedUnitGroup =
        expandedIndex >= 0 ? filteredAndSortedUnits[expandedIndex] : null;
    // Only set when the expanded unit is itself featured — drives the expansion panel's
    // featured outline/tint. Null for non-featured units, which keep the primary outline.
    const expandedFeatured = expandedUnitGroup
        ? getFeaturedConfig(expandedUnitGroup, displayOptions.featuredUnits)
        : null;

    // Row that the expansion occupies (1-based, after the expanded card's row)
    const C = gridColumnCount;
    const expansionRow =
        expandedIndex >= 0 ? Math.floor(expandedIndex / C) + 2 : -1;

    // Single per-group row renderer shared by BOTH the flat path and the
    // market-template sections below — keep them from diverging. `index` is
    // the position within the rendered list (flat table, or section-local in
    // market mode) and only matters for the grid expansion's row placement.
    const renderUnitGroupRow = (unitGroup: UnitGroup, index: number) => {
        const unitId = unitGroup.representative_unit.id;

        // When a full-width sibling is open (promo in either layout, or
        // nearby in sibling mode), explicitly place cards so it can sit on
        // a reserved row (cards at/after the expansion row shift down by
        // one). Layout 2 cards span one column; layout 1 cards span two of
        // its container columns (4/6/8 tracks at 2/3/4 cards-per-row).
        let gridStyle: React.CSSProperties | undefined;
        if (expansionRow > 0) {
            let row = Math.floor(index / C) + 1;
            if (row >= expansionRow) {
                row += 1;
            }
            if (isGridLayout2) {
                gridStyle = { gridRow: row, gridColumn: (index % C) + 1 };
            } else {
                // Layout 1 cards span 2 of the multi-track grid (4/6/8
                // tracks → 2/3/4 cards). At 1 column (mobile) the grid
                // collapses to a single full-width track, so span the full
                // row like the sibling instead of forcing a 2-track span
                // (which would squish the card).
                gridStyle = {
                    gridRow: row,
                    gridColumn:
                        C === 1 ? "1 / -1" : `${(index % C) * 2 + 1} / span 2`,
                };
            }
        }

        const isSoldOutWithNearby =
            unitGroup.available === 0 && unitGroup.show_nearby_units;

        // Row view keeps the inline (Radix Collapsible) nearby expansion.
        if (
            isSoldOutWithNearby &&
            displayOptions.tableLayout === TableLayout.ROW
        ) {
            return (
                <UnavailableUnitItem
                    key={unitId}
                    unitGroup={unitGroup}
                    layout={displayOptions.tableLayout ?? "row"}
                    isNearbyExpanded={nearbyExpandedUnitId === unitId}
                    onNearbyToggle={() => handleNearbyToggle(unitId)}
                />
            );
        }

        // Everything else (incl. grid views) uses UnitItem. The promo
        // expansion renders as a full-width sibling below the row in both
        // grid layouts (cards shift down). Nearby keeps its own hybrid
        // (sibling at 2 cols, card overlay at 1/3/4 cols — UnitItem owns
        // the nearby overlay).
        return (
            <UnitItem
                key={unitId}
                unitGroup={unitGroup}
                layout={displayOptions.tableLayout ?? "row"}
                gridExpandedUnit={
                    gridExpansionEnabled ? gridExpandedUnit : undefined
                }
                onGridPromoExpand={
                    gridExpansionEnabled ? handleGridPromoExpand : undefined
                }
                gridStyle={gridStyle}
                showNearbyButton={gridExpansionEnabled && isSoldOutWithNearby}
                nearbyAsOverlay={gridExpandsAsOverlay}
                isNearbyExpanded={nearbyExpandedUnitId === unitId}
                onNearbyToggle={() => handleGridNearbyToggle(unitId)}
            />
        );
    };

    // Market template (WEB-704): wrap the SAME filtered/sorted/pinned list the
    // flat path renders into per-facility sections (CMS facilityIds order).
    // Filtering, sorting, and featured pinning all happen above, so they keep
    // working within each section; loading/error/no-data states above are
    // untouched. Empty sections keep their header and reuse the table's
    // existing empty-state copy. Grid expansions are disabled entirely in
    // market mode (gridExpansionEnabled above), so the flat-path siblings
    // below never apply here.
    if (marketSections) {
        // Market layout (WEB-704) shows at most the 3 CHEAPEST units per
        // facility (by displayed rate, any size). Other market-mode layouts
        // keep every group.
        const cheapestOnly =
            displayOptions.widgetLayout === WidgetLayout.MARKET;
        return (
            <div className="unit-table unit-table--market">
                {groupByFacility(
                    filteredAndSortedUnits,
                    displayOptions.facilityIds,
                ).map((section) => {
                    const sectionGroups = cheapestOnly
                        ? cheapestUnits(section.unitGroups, 3)
                        : section.unitGroups;
                    return (
                        <FacilitySection
                            key={section.facilityId}
                            facilityId={section.facilityId}
                            hasUnits={sectionGroups.length > 0}
                        >
                            {/* Empty facilities render no unit table — just the
                                header + a View Facility button (FacilitySection
                                handles that when hasUnits is false). */}
                            {sectionGroups.length > 0
                                ? sectionGroups.map(renderUnitGroupRow)
                                : null}
                        </FacilitySection>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="unit-table">
            {filteredAndSortedUnits.map(renderUnitGroupRow)}
            {/* Grid promo/quote expansion — full-width sibling spanning all
                columns, both layouts. Placed at expansionRow with explicit
                grid-row. Layout 1 is form-only (skipSelector) using the card's
                lifted promo; Layout 2 keeps its in-expansion promo selector. */}
            {gridPromoId !== null && expandedUnitGroup && gridExpandedUnit && (
                <GridPromoExpansion
                    key={`promo-${gridPromoId}`}
                    unitGroup={expandedUnitGroup}
                    expandedBy={gridExpandedUnit.expandedBy}
                    gridRow={expansionRow}
                    isOpen={isPromoOpen}
                    skipSelector={!isGridLayout2}
                    selectedPromo={gridExpandedUnit.selectedPromo}
                    featuredColor={expandedFeatured?.color}
                    featuredColorLight={expandedFeatured?.colorLight}
                />
            )}
            {/* Grid sibling mode: nearby-units expansion, same sibling mechanism. */}
            {gridNearbyId !== null && expandedUnitGroup && (
                <GridNearbyExpansion
                    key={`nearby-${gridNearbyId}`}
                    unitGroup={expandedUnitGroup}
                    gridRow={expansionRow}
                    isOpen={isNearbyOpen}
                />
            )}
        </div>
    );
};
